import React, { useState, useEffect, useMemo } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';
import { purchaseAPI, branchAPI } from '../../services/api';
import DateRangeFilter, { filterByDateRange } from './DateRangeFilter';
import LandedCostReport from '../purchase/LandedCostReport';
import PoStatusReport from './PoStatusReport';

const COLORS = ['#24549c', '#3a6fc4', '#FF9800', '#1a3d73', '#4CAF50', '#E65100', '#6A1B9A'];

const PurchaseReport = () => {
  const [pos, setPos] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('vendor');
  const [mode, setMode] = useState('analytics'); // 'analytics' | 'landed'
  const [typeFilter, setTypeFilter] = useState('All');
  const [dateRange, setDateRange] = useState({ from: null, to: null });

  useEffect(() => {
    const fetch = async () => {
      try {
        const [p, b] = await Promise.all([purchaseAPI.getAll(), branchAPI.getAll().catch(() => [])]);
        setPos(p); setBranches(b);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const filtered = useMemo(() => {
    const dateFiltered = filterByDateRange(pos, dateRange);
    return dateFiltered.filter(p => typeFilter === 'All' || p.type === typeFilter);
  }, [pos, typeFilter, dateRange]);

  const totalValue = filtered.reduce((s, p) => s + (p.inr_value || 0), 0);
  const totalPOs = filtered.length;
  const receivedPOs = filtered.filter(p => p.status === 'Received').length;
  const overduePOs = filtered.filter(p => p.status !== 'Received' && p.status !== 'Cancelled' && p.expected_delivery && new Date(p.expected_delivery) < new Date()).length;
  const importValue = filtered.filter(p => p.type === 'import').reduce((s, p) => s + (p.inr_value || 0), 0);

  // Vendor-wise
  const vendorData = useMemo(() => {
    const map = {};
    filtered.forEach(p => {
      const vn = p.vendor_name || 'Unknown';
      if (!map[vn]) map[vn] = { name: vn, value: 0, count: 0, received: 0 };
      map[vn].value += (p.inr_value || 0);
      map[vn].count++;
      if (p.status === 'Received') map[vn].received++;
    });
    return Object.values(map).sort((a, b) => b.value - a.value);
  }, [filtered]);

  // Status-wise
  const statusData = useMemo(() => {
    const map = {};
    filtered.forEach(p => {
      if (!map[p.status]) map[p.status] = { name: p.status, value: 0, count: 0 };
      map[p.status].value += (p.inr_value || 0);
      map[p.status].count++;
    });
    return Object.values(map);
  }, [filtered]);

  // Branch-wise
  const branchData = useMemo(() => {
    const map = {};
    filtered.forEach(p => {
      const br = branches.find(b => b.id === p.branch)?.name || p.branch || 'Unknown';
      if (!map[br]) map[br] = { name: br, value: 0, count: 0, domestic: 0, import: 0 };
      map[br].value += (p.inr_value || 0);
      map[br].count++;
      if (p.type === 'import') map[br].import += (p.inr_value || 0);
      else map[br].domestic += (p.inr_value || 0);
    });
    return Object.values(map).sort((a, b) => b.value - a.value);
  }, [filtered, branches]);

  const currentData = view === 'vendor' ? vendorData : view === 'status' ? statusData : branchData;

  const exportCSV = (data, filename) => {
    if (!data.length) return;
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(r => Object.values(r).join(','));
    const blob = new Blob([[headers, ...rows].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${filename}.csv`; a.click();
  };

  const ModeTabs = () => (
    <div className="flex gap-1.5 mb-5" data-testid="purchase-report-mode-tabs">
      {[{ id: 'analytics', label: 'Purchase Analytics' }, { id: 'po-status', label: 'PO Status Report' }, { id: 'landed', label: 'Landed Cost' }].map(t => (
        <button key={t.id} onClick={() => setMode(t.id)} data-testid={`purchase-mode-${t.id}`}
          className={`px-4 py-2 text-xs font-semibold rounded-lg transition-all ${mode === t.id ? 'text-white' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'}`}
          style={mode === t.id ? { backgroundColor: '#24549c' } : {}}>{t.label}</button>
      ))}
    </div>
  );

  if (mode === 'landed') {
    return (<div><ModeTabs /><LandedCostReport /></div>);
  }

  if (mode === 'po-status') {
    return (<div><ModeTabs /><PoStatusReport /></div>);
  }

  if (loading) return <div><ModeTabs /><div className="flex items-center justify-center h-40"><Loader2 className="w-5 h-5 animate-spin" style={{ color: '#24549c' }} /><span className="ml-2 text-xs text-gray-500">Loading...</span></div></div>;

  return (
    <div>
      <ModeTabs />
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Purchase Analytics</h2>
          <p className="text-xs text-gray-500">Vendor, branch & status breakdown of procurement spend</p>
        </div>
        <button onClick={() => exportCSV(currentData, `purchase-${view}`)} className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg" style={{ backgroundColor: '#24549c' }}><Download className="w-3.5 h-3.5" /> Export CSV</button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-5">
        {[
          { l: 'Total PO Value', v: `₹${(totalValue / 100000).toFixed(1)}L` },
          { l: 'Total POs', v: totalPOs },
          { l: 'Received', v: receivedPOs },
          { l: 'Overdue', v: overduePOs, warn: overduePOs > 0 },
          { l: 'Import Value', v: `₹${(importValue / 100000).toFixed(1)}L` },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 p-4">
            <p className={`text-lg font-bold ${s.warn ? 'text-red-600' : 'text-gray-900'}`} style={{ fontFamily: 'DM Sans' }}>{s.v}</p>
            <p className="text-[10px] text-gray-500">{s.l}</p>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <DateRangeFilter onChange={setDateRange} />
        <div className="flex gap-1.5 ml-auto">
          {[{ id: 'vendor', label: 'Vendor-wise' }, { id: 'status', label: 'Status-wise' }, { id: 'branch', label: 'Branch-wise' }].map(t => (
            <button key={t.id} onClick={() => setView(t.id)} className={`px-3 py-1.5 text-[11px] font-medium rounded-full transition-all ${view === t.id ? 'text-white' : 'text-gray-600 bg-gray-100'}`} style={view === t.id ? { backgroundColor: '#24549c' } : {}}>{t.label}</button>
          ))}
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg">
          <option value="All">All Types</option>
          <option value="domestic">Domestic</option>
          <option value="import">Import</option>
        </select>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-5 mb-5">
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4">{view === 'vendor' ? 'Vendor' : view === 'status' ? 'Status' : 'Branch'} — Value (₹)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={currentData.slice(0, 10)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#9CA3AF' }} angle={-15} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} tickFormatter={v => `${(v / 100000).toFixed(0)}L`} />
              <Tooltip formatter={v => `₹${v.toLocaleString()}`} contentStyle={{ borderRadius: '8px', fontSize: '11px' }} />
              {view === 'branch' ? (
                <><Bar dataKey="domestic" name="Domestic" fill="#24549c" radius={[3, 3, 0, 0]} /><Bar dataKey="import" name="Import" fill="#FF9800" radius={[3, 3, 0, 0]} /><Legend wrapperStyle={{ fontSize: '10px' }} /></>
              ) : (
                <Bar dataKey="value" fill="#24549c" radius={[3, 3, 0, 0]} />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4">Distribution</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={currentData.filter(d => d.value > 0)} cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3} dataKey="value" nameKey="name">
                {currentData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={v => `₹${v.toLocaleString()}`} contentStyle={{ borderRadius: '8px', fontSize: '11px' }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-2 mt-2">
            {currentData.filter(d => d.value > 0).slice(0, 7).map((d, i) => (
              <span key={i} className="flex items-center gap-1 text-[10px] text-gray-500"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />{d.name}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <table className="w-full text-xs">
          <thead><tr className="border-b border-gray-100 bg-gray-50">
            {['Name', 'POs', 'Value (₹)', ...(view === 'vendor' ? ['Received'] : view === 'branch' ? ['Domestic', 'Import'] : [])].map(h => (
              <th key={h} className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {currentData.map((r, i) => (
              <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                <td className="px-4 py-3 font-semibold text-gray-900">{r.name}</td>
                <td className="px-4 py-3 text-gray-500">{r.count}</td>
                <td className="px-4 py-3 font-semibold">₹{r.value.toLocaleString()}</td>
                {view === 'vendor' && <td className="px-4 py-3 text-gray-500">{r.received}/{r.count}</td>}
                {view === 'branch' && <><td className="px-4 py-3">₹{(r.domestic || 0).toLocaleString()}</td><td className="px-4 py-3">₹{(r.import || 0).toLocaleString()}</td></>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PurchaseReport;
